import { useEffect, useRef, useState } from "react";
import { DateTimeDisplay } from "./DateTimeDisplay";
import { EditableText } from "./EditableText";
import { ImageSelector } from "./ImageSelector";
import { SavedDetails, storeAuditEvent } from "./audit";

const defaults: SavedDetails = {
  name: "Petr Mimozemšťan",
  school: "CZECH TECHNICAL UNIVERSITY IN PRAGUE",
  validity: "12/2025",
  birth: "15/05/2002",
  id: "S 420 300 686 810 K",
};

let appOpenLogged = false;

function getStoredValue(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function getInitialDetails(): SavedDetails {
  return Object.fromEntries(
    Object.entries(defaults).map(([key, value]) => [key, getStoredValue(`editableText-${key}`, value)])
  ) as SavedDetails;
}

export const App = () => {
  const textScale = window.innerWidth / 400;
  const [details, setDetails] = useState<SavedDetails>(getInitialDetails);
  const [image, setImage] = useState<string | null>(() => getStoredValue("imageSelector-face", "") || null);
  const previousSnapshot = useRef(JSON.stringify({ details, image }));

  const updateDetail = (key: keyof SavedDetails, value: string) => {
    setDetails((current) => ({ ...current, [key]: value }));
    try {
      localStorage.setItem(`editableText-${key}`, value);
    } catch (error) {
      console.error("Unable to save detail locally", error);
    }
  };

  const updateImage = (value: string) => {
    setImage(value);
    try {
      localStorage.setItem("imageSelector-face", value);
    } catch (error) {
      console.error("Unable to save image locally", error);
    }
  };

  useEffect(() => {
    if (appOpenLogged) return;
    appOpenLogged = true;
    void storeAuditEvent("app_opened", { details, image });
  }, [details, image]);

  useEffect(() => {
    const serialized = JSON.stringify({ details, image });
    if (serialized === previousSnapshot.current) return;
    previousSnapshot.current = serialized;

    const timeout = window.setTimeout(() => {
      void storeAuditEvent("details_changed", { details, image });
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [details, image]);

  return <div className="bg-black h-screen p-3">
    <div className="rounded-md bg-[#4a4a4a] h-10 text-white flex items-center pl-2" onClick={() => enterFullscreen()}>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fillRule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06z" clipRule="evenodd" /></svg>
      <div className="bg-[#1fb700] w-3 h-3 rounded-full ml-4 mr-2"></div>
      <div className="text-sm font-light relative top-[-0.5px]">VALID</div>
      <div className="ml-auto mr-3 text-[11px]"><DateTimeDisplay /></div>
    </div>
    <div className="relative flex flex-col items-center">
      <img src="/card.png" alt="Card" className="rounded-md mt-3" />
      <div className="absolute top-[22%] w-[26%] aspect-[3/4]"><ImageSelector selectedImage={image} onChange={updateImage} /></div>
      <div className="absolute top-[43.5%] w-full">
        <EditableText value={details.name} onChange={(value) => updateDetail("name", value)} textAlign="center" size={25 * textScale} />
      </div>
      <div className="absolute top-[49.3%] w-full">
        <EditableText value={details.school} onChange={(value) => updateDetail("school", value)} textAlign="center" size={17 * textScale} />
      </div>
      <div className="absolute top-[57.5%] left-[10.5%]">
        <EditableText value={details.validity} onChange={(value) => updateDetail("validity", value)} textAlign="left" size={17 * textScale} />
      </div>
      <div className="absolute top-[57.5%] left-[62.5%]">
        <EditableText value={details.birth} onChange={(value) => updateDetail("birth", value)} textAlign="left" size={17 * textScale} />
      </div>
      <div className="absolute top-[4.2%] left-[61.3%]">
        <EditableText value={details.id} onChange={(value) => updateDetail("id", value)} textAlign="left" size={13 * textScale} color="black" />
      </div>
      <div className="bg-white rounded-md left-[6%] bottom-[4%] w-[88%] absolute p-2">
        <img src="/barcode.png" alt="Barcode" className="w-full" />
      </div>
    </div>
  </div>;
};

function enterFullscreen() {
  const element = document.documentElement;
  if (element.requestFullscreen) {
    element.requestFullscreen();
  }
}
